import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Badge, Heading, Button, Input, FormControl,
  FormLabel, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, Spinner, Center, Flex,
  IconButton, Tooltip, useToast, Code,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiRefreshCw, FiCopy } from 'react-icons/fi';

function KeyRow({ keyObj, onDelete }) {
  const toast = useToast();

  const copy = () => {
    navigator.clipboard.writeText(keyObj.key);
    toast({ title: 'Copied', status: 'success', duration: 1500, isClosable: true });
  };

  return (
    <Box bg="white" borderRadius="md" px={4} py={3} borderWidth="1px" borderColor="gray.100" boxShadow="sm">
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={1}>
          <HStack>
            <Text color="gray.800" fontWeight="600">{keyObj.name}</Text>
            <Badge colorScheme={keyObj.is_active ? 'green' : 'red'} fontSize="xs">
              {keyObj.is_active ? 'active' : 'inactive'}
            </Badge>
          </HStack>
          <HStack>
            <Code bg="purple.50" color="purple.700" fontSize="xs" px={2} py={1} borderRadius="sm">
              {keyObj.key}
            </Code>
            <Tooltip label="Copy key">
              <IconButton icon={<FiCopy />} size="xs" variant="ghost" colorScheme="purple" onClick={copy} aria-label="Copy" />
            </Tooltip>
          </HStack>
          {keyObj.last_used_at && (
            <Text color="gray.400" fontSize="xs">
              Last used: {new Date(keyObj.last_used_at).toLocaleString()}
            </Text>
          )}
        </VStack>
        <Tooltip label="Delete key">
          <IconButton
            icon={<FiTrash2 />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => onDelete(keyObj.id)}
            aria-label="Delete"
          />
        </Tooltip>
      </Flex>
    </Box>
  );
}

function IngestKeys({ token, apiBase }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const authHeaders = { Authorization: `Token ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${apiBase}/api/v1/ingest-keys/`, { headers: authHeaders });
      if (resp.ok) {
        const data = await resp.json();
        setKeys(data.results || data);
      }
    } finally {
      setLoading(false);
    }
  }, [token, apiBase]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const key = `sk-${crypto.randomUUID().replace(/-/g, '')}`;
      const resp = await fetch(`${apiBase}/api/v1/ingest-keys/`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ name: newName.trim(), key }),
      });
      if (resp.ok) {
        toast({ title: 'Key created', status: 'success', duration: 2000 });
        setNewName('');
        onClose();
        load();
      } else {
        toast({ title: 'Failed to create key', status: 'error', duration: 3000 });
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch(`${apiBase}/api/v1/ingest-keys/${id}/`, { method: 'DELETE', headers: authHeaders });
    toast({ title: 'Key deleted', status: 'info', duration: 2000 });
    load();
  };

  return (
    <VStack spacing={4} align="stretch">
      <Flex justify="space-between" align="center">
        <Box>
          <Heading size="lg" color="gray.800">Ingest Keys</Heading>
          <Text color="gray.500" mt={1}>Manage credentials for SDK event ingestion</Text>
        </Box>
        <HStack>
          <Tooltip label="Refresh">
            <IconButton
              icon={<FiRefreshCw />}
              size="sm"
              colorScheme="purple"
              variant="ghost"
              onClick={load}
              isLoading={loading}
              aria-label="Refresh"
            />
          </Tooltip>
          <Button leftIcon={<FiPlus />} colorScheme="purple" size="sm" onClick={onOpen}>
            New Key
          </Button>
        </HStack>
      </Flex>

      <Box bg="purple.50" borderRadius="md" px={4} py={3} borderWidth="1px" borderColor="purple.100">
        <Text color="purple.700" fontSize="sm">
          Send ingest keys as the <Code fontSize="xs" bg="white" color="purple.700" px={1}>X-Ingest-Key</Code> header in client SDK requests.
        </Text>
      </Box>

      {loading ? (
        <Center py={10}><Spinner color="purple.500" /></Center>
      ) : keys.length === 0 ? (
        <Center py={10}><Text color="gray.400">No keys yet. Create one to start receiving events.</Text></Center>
      ) : (
        <VStack spacing={2} align="stretch">
          {keys.map(k => <KeyRow key={k.id} keyObj={k} onDelete={handleDelete} />)}
        </VStack>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="gray.800">Create Ingest Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Key name</FormLabel>
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. portal-production"
                focusBorderColor="purple.500"
                onKeyDown={e => e.key === 'Enter' && handleCreate()}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button colorScheme="purple" onClick={handleCreate} isLoading={creating}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

export default IngestKeys;
